import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useDevices,
  useDaily,
  useDailyEvent,
  DailyVideo,
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';

import './HairCheck.css';
import {
  Select,
  Stack,
  Title,
  Button,
  Group,
  Box,
  Space,
  Text,
  Paper,
  LoadingOverlay,
  Loader,
  Progress,
  Badge,
} from '@mantine/core';
import { primaryColor } from '../../constants/theme';
import { useApp } from '../../context/AppContext';
import { IconCameraOff } from '@tabler/icons-react';

interface HairCheckProps {
  joinCall: (username: string) => void;
  cancelCall: () => void;
}

export default function HairCheck({ joinCall, cancelCall }: HairCheckProps) {
  const localSessionId = useLocalSessionId();
  const initialUsername = useParticipantProperty(localSessionId, 'user_name');
  const {
    currentCam,
    currentMic,
    currentSpeaker,
    microphones,
    speakers,
    cameras,
    setMicrophone,
    setCamera,
    setSpeaker,
  } = useDevices();
  const callObject = useDaily();
  const [username, setUsername] = useState(initialUsername);
  const navigate = useNavigate();
  const { isTabletAndBelow, scrollAreaHeight } = useApp();

  const [getUserMediaError, setGetUserMediaError] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [micIsWorking, setMicIsWorking] = useState(false);
  const [speakerIsWorking, setSpeakerIsWorking] = useState(false);
  const [isPlayingTestSound, setIsPlayingTestSound] = useState(false);

  useEffect(() => {
    setUsername(initialUsername);
  }, [initialUsername]);

  // Monitor audio levels using Web Audio API
  useEffect(() => {
    if (!callObject || !localSessionId) return;

    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let microphone: MediaStreamAudioSourceNode | null = null;
    let animationId: number | null = null;

    const setupAudioCapture = async () => {
      try {
        // Get the current microphone stream
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;

        microphone = audioContext.createMediaStreamSource(stream);
        microphone.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateLevel = () => {
          if (!analyser) return;

          analyser.getByteFrequencyData(dataArray);

          // Calculate average frequency magnitude
          const average = dataArray.reduce((a, b) => a + b) / bufferLength;
          const normalized = Math.min((average / 256) * 150, 100); // Scale and cap at 100%

          setAudioLevel(normalized);
          setMicIsWorking(normalized > 5); // Threshold for considering it "working"

          animationId = requestAnimationFrame(updateLevel);
        };

        updateLevel();
      } catch (error) {
        console.error('Error accessing microphone for audio capture:', error);
        setMicIsWorking(false);
        setAudioLevel(0);
      }
    };

    setupAudioCapture();

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      if (microphone) microphone.disconnect();
      if (analyser) analyser.disconnect();
      if (audioContext) audioContext.close();
    };
  }, [callObject, localSessionId]);

  useDailyEvent(
    'camera-error',
    useCallback(() => {
      setGetUserMediaError(true);
    }, [])
  );

  const handleChange = (e: any) => {
    setUsername(e.target.value);
    callObject?.setUserName(e.target.value);
  };

  const handleJoin = (e: any) => {
    e.preventDefault();
    console.log('Joining call with username:', username.trim());
    console.log(
      'Microphone status:',
      micIsWorking ? 'Working' : 'Not detected'
    );
    console.log('Audio level:', audioLevel, '%');
    joinCall(username.trim());
  };

  const handleCancel = () => {
    cancelCall();
    navigate('/bookings');
  };

  const playTestSound = async () => {
    try {
      setIsPlayingTestSound(true);
      setSpeakerIsWorking(false);

      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Play a 1000Hz tone for 500ms
      oscillator.frequency.value = 1000;
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.5
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);

      // Mark speakers as working
      await new Promise((resolve) => setTimeout(resolve, 100));
      setSpeakerIsWorking(true);

      await new Promise((resolve) => setTimeout(resolve, 400));
      setIsPlayingTestSound(false);
    } catch (error) {
      console.error('Error playing test sound:', error);
      setIsPlayingTestSound(false);
    }
  };

  const updateMicrophone = (e: any) => {
    setMicrophone(e.target.value);
  };

  const updateSpeakers = (e: any) => {
    setSpeaker(e.target.value);
  };

  const updateCamera = (e: any) => {
    setCamera(e.target.value);
  };

  return getUserMediaError ? (
    <Stack align="center" h={isTabletAndBelow ? scrollAreaHeight : 'auto'}>
      <Paper maw={1200} w="100%" mih="400" h="100%">
        <Stack justify="center" align="center" gap={5} p={100}>
          <IconCameraOff size={100} color={primaryColor} />
          <Title order={4} fw="normal">
            Camera or microphone is off
          </Title>
          <Text c="dimmed">
            Please enable your camera and microphone to join the call.
          </Text>
        </Stack>
      </Paper>
    </Stack>
  ) : (
    <Stack justify="center" align="center" gap={5}>
      <Box h={isTabletAndBelow ? scrollAreaHeight : 'auto'} p={20} bg={'white'}>
        <form onSubmit={handleJoin}>
          <Title order={3} pb={10} fw="normal">
            Setup your hardware
          </Title>
          {!localSessionId && (
            <LoadingOverlay
              visible={true}
              loaderProps={{
                children: (
                  <Stack justify="center" align="center" gap={10}>
                    <Loader size="md" color={primaryColor} />
                    <Title order={3} fw="normal">
                      Loading Camera...
                    </Title>
                  </Stack>
                ),
              }}
            />
          )}
          {localSessionId && (
            <Stack h={300}>
              <DailyVideo sessionId={localSessionId} mirror type={'video'} />
            </Stack>
          )}

          <Space h={10} />

          {/* Microphone select */}
          <Select
            w="100%"
            label="Microphone:"
            name="micOptions"
            id="micSelect"
            onChange={(value) => updateMicrophone({ target: { value } })}
            value={currentMic?.device?.deviceId}
            data={microphones.map((mic) => ({
              value: mic.device.deviceId,
              label: mic.device.label,
            }))}
          />

          {/* Audio level indicator */}
          <Box mt={10}>
            <Group justify="space-between" mb={5}>
              <Text size="sm" fw={500}>
                Audio Level:
              </Text>
              <Badge size="sm" color={micIsWorking ? 'green' : 'gray'}>
                {micIsWorking ? 'Microphone detected' : 'No audio detected'}
              </Badge>
            </Group>
            <Progress
              value={audioLevel}
              color={micIsWorking ? 'green' : 'gray'}
            />
          </Box>

          {/* Speakers select */}
          <Select
            w="100%"
            label="Speakers:"
            name="speakersOptions"
            id="speakersSelect"
            onChange={(value) => updateSpeakers({ target: { value } })}
            value={currentSpeaker?.device?.deviceId}
            data={speakers.map((speaker) => ({
              value: speaker.device.deviceId,
              label: speaker.device.label,
            }))}
          />

          {/* Speaker test */}
          <Box mt={10}>
            <Group justify="space-between" mb={5}>
              <Text size="sm" fw={500}>
                Test Speakers:
              </Text>
              <Badge size="sm" color={speakerIsWorking ? 'green' : 'gray'}>
                {speakerIsWorking ? 'Speakers working' : 'Not tested'}
              </Badge>
            </Group>
            <Button
              onClick={playTestSound}
              disabled={isPlayingTestSound}
              variant="light"
              color={primaryColor}
              w="100%"
            >
              {isPlayingTestSound ? 'Playing test sound...' : 'Play test sound'}
            </Button>
          </Box>

          {/* Camera select */}
          <Select
            w="100%"
            label="Camera:"
            name="cameraOptions"
            id="cameraSelect"
            onChange={updateCamera}
            value={currentCam?.device?.deviceId}
            data={cameras.map((camera) => ({
              value: camera.device.deviceId,
              label: camera.device.label,
            }))}
          />

          <Space h={20} />

          <Group justify="space-between" align="center" gap={10} w="100%">
            <Button
              onClick={handleCancel}
              className="cancel-call"
              type="button"
              variant="outline"
              color={primaryColor}
              w="48%"
            >
              Cancel
            </Button>
            <Button
              onClick={handleJoin}
              type="submit"
              color={primaryColor}
              w="48%"
            >
              Join call
            </Button>
          </Group>
        </form>
      </Box>
    </Stack>
  );
}
